import { documentReviewUrl } from './api';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
type Settings = {
  version: number;
  model_fallback_enabled?: boolean;
  model_tags?: string[];
  phase?: string[];
  kind?: string[];
  subject?: string[];
  sender_rules?: Array<{ sender: string; field: string; label: string; enabled: boolean }>;
  sender_confirmation_count?: number;
  patient_match_threshold?: number;
};
export function DocumentReviewSettings() {
  const { organization } = useAuth();
  const org = organization?.organization_id;
  const cache = useQueryClient();
  const url = documentReviewUrl(org);
  const [label, setLabel] = useState('');
  const [field, setField] = useState<'phase' | 'kind' | 'subject'>('kind');
  const settings = useQuery({
    queryKey: ['document-review-settings', org],
    enabled: !!org,
    queryFn: async () => (await clientApi.get<Settings>(`${url}/settings`)).data,
  });
  const stats = useQuery({
    queryKey: ['document-review-statistics', org],
    enabled: !!org,
    queryFn: async () =>
      (
        await clientApi.get<{
          accepted: number;
          rejected: number;
          sender_rule_proposals: Array<{
            sender: string;
            field: string;
            label: string;
            confirmations: number;
            enabled: boolean;
          }>;
        }>(`${url}/statistics`)
      ).data,
  });
  const save = useMutation({
    mutationFn: async (change: Partial<Settings>) =>
      clientApi.put(`${url}/settings`, {
        expected_version: settings.data!.version,
        model_fallback_enabled: settings.data!.model_fallback_enabled ?? false,
        phase: settings.data!.phase ?? [],
        kind: settings.data!.kind ?? [],
        subject: settings.data!.subject ?? [],
        sender_rules: settings.data!.sender_rules ?? [],
        sender_confirmation_count: settings.data!.sender_confirmation_count ?? 5,
        patient_match_threshold: settings.data!.patient_match_threshold ?? 0.95,
        ...change,
      }),
    onSuccess: () => {
      setLabel('');
      cache.invalidateQueries({ queryKey: ['document-review-settings', org] });
      cache.invalidateQueries({ queryKey: ['document-review', org] });
    },
  });
  return (
    <details className="border rounded p-3">
      <summary>Practice taxonomy and suggestion statistics</summary>
      <label className="block text-sm">
        <input
          type="checkbox"
          checked={settings.data?.model_fallback_enabled ?? false}
          disabled={!settings.data || save.isPending}
          onChange={(e) => save.mutate({ model_fallback_enabled: e.target.checked })}
        />{' '}
        Enable reviewed model classification fallback for future OCR completions
      </label>
      <p className="text-sm">
        Uses the configured extraction model only for missing labels and allowed tags. Exact source quotations are
        required. Suggestions never assign patients or accept clinical results. Allowed model tags come from existing
        practice document tags and enabled sender-tag rules.
      </p>
      <p>
        Accepted: {stats.data?.accepted ?? '—'} · Rejected: {stats.data?.rejected ?? '—'}
      </p>
      <p className="text-sm">
        Practice changes require tag-management permission. Sender rules suggest labels; they do not assign patients or
        accept clinical results.
      </p>
      <label>
        Extend taxonomy
        <select value={field} onChange={(e) => setField(e.target.value as typeof field)}>
          <option value="phase">Phase</option>
          <option value="kind">Kind</option>
          <option value="subject">Subject</option>
        </select>
      </label>
      <Input aria-label="New taxonomy label" value={label} maxLength={100} onChange={(e) => setLabel(e.target.value)} />
      <Button
        disabled={!label.trim() || !settings.data || save.isPending}
        onClick={() => save.mutate({ [field]: [...(settings.data?.[field] ?? []), label.trim()] })}>
        Add label
      </Button>
      {stats.data?.sender_rule_proposals.map((p) => (
        <div key={`${p.sender}:${p.field}:${p.label}`} className="border p-2">
          <p>
            {p.sender}: {p.field} → {p.label} ({p.confirmations} distinct documents confirmed)
          </p>
          <Button
            disabled={!settings.data || save.isPending}
            onClick={() =>
              save.mutate({
                sender_rules: [
                  ...(settings.data?.sender_rules ?? []),
                  { sender: p.sender, field: p.field, label: p.label, enabled: true },
                ],
              })
            }>
            Review and enable sender suggestion rule
          </Button>
        </div>
      ))}
      {save.isError && <p role="alert">Settings not saved. Check permission or refresh for the latest version.</p>}
    </details>
  );
}
